<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex flex-row">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.back')"
            icon="icon-outline_arrow-left"
            @click="$router.push({ name: 'Email Service Alert' })"
          />
          <McButton
            variant="primary"
            :text="$t('global.save')"
            icon="icon-outline_save"
            @click="saveChanges"
          />
        </div>
      </div>
    </div>

    <b-skeleton-wrapper :loading="$route.params.id && !isLoaded">
      <template #loading>
        <FormLoading />
      </template>

      <div class="mb-4">
        <h4 class="card-title">
          {{ $t('maintenance.frontPage.title.scheduleInfo') }}
        </h4>
      </div>

      <b-card>
        <b-row class="mb-3">
          <b-col md="6">
            <FormulateForm>
              <FormulateInput
                v-model="report.scheduleName"
                :label="$t('report.title.scheduleName')"
                :placeholder="$t('report.title.scheduleName')"
                :validation="'required'"
                error-behavior="blur"
              />
            </FormulateForm>
          </b-col>
        </b-row>
      </b-card>

      <div class="mb-4">
        <h4 class="card-title">
          {{ $t('maintenance.field.schedulingPeriod') }}
        </h4>
      </div>

      <b-card>
        <b-row class="mb-3">
          <b-col md="6">
            <FormulateForm class="mc-custom-radio-schedule">
              <b-form-radio v-model="report.period" name="schedulePeriod" value="d" class="mb-2">
                <b-row>
                  <b-col md="2">
                    {{ $t('global.time.daily') }}
                  </b-col>
                  <b-col md="10">
                    <date-picker
                      v-model="timeDay"
                      format="HH:mm"
                      value-type="format"
                      type="time"
                      placeholder="HH:mm"
                      :disabled="report.period === 'w'"
                      :lang="datePickerLang"
                    />
                  </b-col>
                </b-row>
              </b-form-radio>
              <b-form-radio v-model="report.period" name="schedulePeriod" value="w">
                <b-row>
                  <b-col md="2">
                    {{ $t('global.time.weekly') }}
                  </b-col>
                  <b-col md="5">
                    <multiselect
                      v-model="report.noOfDay"
                      :placeholder="`${$t('global.form.select')} ${$t('global.time.days')}`"
                      :options="reminderPeriodeOptions"
                      track-by="value"
                      openDirection="bottom"
                      label="text"
                      value="true"
                      :disabled="report.period == 'd'"
                      :class="{ 'is-invalid': $v.report.noOfDay.$error }"
                    />
                    <div v-if="$v.report.noOfDay.$error" class="invalid-feedback">
                      <span v-if="!$v.report.noOfDay.required">
                        {{ $t('report.object.daySelection') }}
                        {{ $t('global.validation.required') }}
                      </span>
                    </div>
                  </b-col>
                  <b-col md="5">
                    <date-picker
                      v-model="timeWeek"
                      format="HH:mm"
                      value-type="format"
                      type="time"
                      placeholder="HH:mm"
                      :disabled="report.period === 'd'"
                      :lang="datePickerLang"
                    />
                  </b-col>
                </b-row>
              </b-form-radio>
            </FormulateForm>
          </b-col>
        </b-row>
      </b-card>

      <div class="d-flex justify-content-between mb-4 align-items-center">
        <h4 class="card-title">
          {{ $t('global.object.fleetGroup') }}
        </h4>
        <div class="float-end d-flex">
          <McButton
            v-if="selectedIdsLength"
            variant="secondary-red"
            :text="$t('global.delete')"
            icon="icon-outline_trash"
            class="mx-2 mb-2 ms-auto"
            @click="deleteItems"
          />
          <McButtonValueHelp
            class="mb-2 ms-auto"
            icon="icon-fill_plus"
            :text="$t('global.create')"
            variant="primary"
            :label="$t('global.object.fleetGroup')"
            :multiple="true"
            trackBy="id"
            :listGetter="resourcefulQuery.vGroups"
            :tableFields="optionsFields"
            @selected="selectedVGroups"
          />
        </div>
      </div>

      <b-card>
        <b-row align-h="end">
          <b-col md="8" xl="10">
            <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.object.selectedRow') }}
              <a class="color-primary" @click="handleUnselectAll">{{
                $t('global.action.clearAll')
              }}</a>
            </h6>
          </b-col>
          <b-col md="4" xl="2" class="d-flex flex-row-reverse mc-table-view-wrapper mt-0">
            <div class="clearfix">
              <!-- Search -->
              <div class="float-end">
                <div class="mc-table-view">
                  <div id="tickets-table_filter" class="dataTables_filter text-md-end">
                    <label
                      class="d-inline-flex align-items-center mc-table-view__search"
                      @click="focusSearch"
                    >
                      <i class="mc-fa fas fa-search"></i>
                      <b-form-input
                        v-model="filter"
                        type="search"
                        :placeholder="$t('global.search') + '...'"
                        class="ms-2"
                        ref="searchInput"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <!-- End search -->
            </div>
          </b-col>
        </b-row>

        <!-- Table -->
        <div class="table-responsive my-3 border border-1">
          <b-table
            small
            show-empty
            :fields="fields"
            :items="itemDatas"
            class="datatables"
            responsive="sm"
            sort-by="sortBy"
            empty-table
            :empty-text="$t('global.emptyTable')"
            :per-page="perPage"
            :current-page="currentPage"
            :filter="filter"
            :filter-included-fields="filterOn"
            @filtered="onFiltered"
          >
            <template #head(checkbox)>
              <div class="form-check" style="width: 5px">
                <b-form-checkbox
                  v-model="selectedAll"
                  :disabled="isBusy"
                  @change="handleSelectAll"
                />
              </div>
            </template>

            <template #cell(checkbox)="data">
              <div class="form-check">
                <b-form-checkbox
                  :checked="selectedIds[data.item[trackBy]]"
                  :disabled="isBusy"
                  @change="(event) => handleSelect(event, data)"
                />
              </div>
            </template>
            <template #cell(fleetGroup)="data">
              {{ data.item.name }}
            </template>
            <template #cell(totalVehicle)="data">
              {{ data.value }}
            </template>
          </b-table>
        </div>

        <div class="row">
          <div class="col-sm-12 col-md-6">
            <div id="tickets-table_length" class="dataTables_length">
              <label class="d-inline-flex align-items-center">
                {{ $t('global.text.displays') }}
                <b-form-select
                  v-model="perPage"
                  class="form-select form-select-sm"
                  size="sm"
                  :options="pageOptions"
                />
                {{ $t('global.text.entries') }}.
              </label>
            </div>
          </div>
          <div class="col-sm-12 col-md-6">
            <div class="dataTables_paginate paging_simple_numbers float-end">
              <ul class="pagination pagination-rounded mb-0">
                <!-- pagination -->
                <b-pagination
                  v-model="currentPage"
                  :total-rows="rows || totalRows"
                  :per-page="perPage"
                />
              </ul>
            </div>
          </div>
        </div>
      </b-card>

      <Recipients :isAdding="!isEdit" />
    </b-skeleton-wrapper>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import Multiselect from 'vue-multiselect'
import moment from 'moment'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { Helper } from '@/mixin/helper'
import {
  GET_DETAIL_REPORT_SCHEDULER,
  CREATE_REPORT_SCHEDULER,
  UPDATE_REPORT_SCHEDULER,
} from '@/services/api/report/report'
import { MixPanel } from '@/mixin/mixpanel'
import datePickerLang from '@/helpers/date-picker-lang'
import McButtonValueHelp from '@/components/McButtonValueHelp'
import McButton from '@/components/McButton'
import Recipients from './Recipients'
import FormLoading from './formloading.vue'
import { GET_VGROUPS_PAGINATED } from '@/services/api/vehicle/vgroup'
import { mapGetters } from 'vuex'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Servis',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    Multiselect,
    McButtonValueHelp,
    McButton,
    Recipients,
    FormLoading,
  },

  mixins: [Helper, MixPanel],

  apollo: {
    reportScheduler: {
      query: GET_DETAIL_REPORT_SCHEDULER,
      variables() {
        return {
          filter: {
            id: parseInt(this.$route.params.id),
          },
        }
      },
      skip() {
        return !this.$route.params.id
      },
      result({ data, loading }) {
        if (loading) return
        if (!data.reportScheduler.isSuccessful) {
          this.$mcToast.warning(data.reportScheduler.message)
        }

        this.initEditPage(data.reportScheduler.reportScheduler)
      },
    },
  },

  data() {
    return {
      reportScheduler: {},
      isLoaded: false,
      isEdit: false,
      timeDay: '',
      timeWeek: '',
      report: {
        period: '',
        time: '',
        noOfDay: '',
        scheduleName: '',
        recipients: [],
        vgroup: [],
      },
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      filteredItems: [],
      ids: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      trackBy: 'id',
      isBusy: false,
      selectedDatas: [],
      fleetGroup: this.$store.state.access.vGroups,
      fleetGroupIds: [],
      filteredFleetGroup: [],
    }
  },

  validations: {
    report: {
      scheduleName: { required },
      noOfDay: {
        required: requiredIf(function () {
          return this.report.period == 'w'
        }),
      },
    },
  },

  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    title() {
      return this.$route.params.id === undefined
        ? this.$t('maintenance.frontPage.title.addScheduleReminder')
        : this.$t('maintenance.frontPage.title.detailScheduleReminder')
    },

    items() {
      return [
        {
          text: this.$t('maintenance.title'),
          to: { name: 'Service' },
        },
        {
          text: this.$t('maintenance.frontPage.title.serviceAlert'),
          to: { name: 'List Service Alert' },
        },
        {
          text: this.$t('global.reminderDelivery'),
          to: { name: 'Email Service Alert' },
        },
        {
          text:
            this.$route.params.id === undefined
              ? this.$t('maintenance.frontPage.title.addScheduleReminder')
              : this.$t('maintenance.frontPage.title.detailScheduleReminder'),
        },
      ]
    },

    datePickerLang() {
      return datePickerLang()
    },

    reminderPeriodeOptions() {
      return [
        { value: 0, text: this.$t('global.days.sunday') },
        { value: 1, text: this.$t('global.days.monday') },
        { value: 2, text: this.$t('global.days.tuesday') },
        { value: 3, text: this.$t('global.days.wednesday') },
        { value: 4, text: this.$t('global.days.thursday') },
        { value: 5, text: this.$t('global.days.friday') },
        { value: 6, text: this.$t('global.days.saturday') },
      ]
    },

    fields() {
      return [
        {
          key: 'checkbox',
          sortable: false,
          thClass: 'mc-table--30',
        },
        {
          key: 'fleetGroup',
          label: this.$t('global.object.fleetGroup'),
          sortable: true,
        },
        {
          key: 'totalVehicle',
          label: this.$t('global.object.totalFleet'),
          sortable: true,
        },
      ]
    },

    itemDatas() {
      return this.report.vgroup
    },

    rows() {
      return this.report.vgroup.length
    },

    optionsFields() {
      return [
        {
          key: 'name',
          label: this.$t('global.object.fleetGroup'),
          sortable: true,
        },
        {
          key: 'totalVehicle',
          label: this.$t('global.object.totalFleet'),
          sortable: true,
        },
      ]
    },

    resourcefulQuery() {
      return {
        vGroups: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: GET_VGROUPS_PAGINATED,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                excludeVgroupId: this.fleetGroupIds,
              },
            },
          })

          const data = {
            metadata: {
              total_count: res.data.vgroupsPaginated.vgroups.pagination.total,
              page: res.data.vgroupsPaginated.vgroups.pagination.page,
            },
            data: {
              paginated_result: res.data.vgroupsPaginated.vgroups.data,
            },
          }

          return data
        },
      }
    },
  },

  mounted() {
    this.$apollo.queries.reportScheduler.refetch()
    setTimeout(() => {
      this.isLoaded = true
    }, 1000)

    this.ids = this.$store.state.access.vGroups.map((item) => {
      return item.id
    })
  },

  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    initEditPage(item) {
      this.isEdit = true

      this.report = {
        period: item.period,
        time: moment(item.time, 'HH:mm:ss').format('HH:mm'),
        noOfDay:
          item.noOfDay !== null
            ? this.reminderPeriodeOptions.find((option) => option.value === item.noOfDay)
            : null,
        scheduleName: item.scheduleName,
        vgroup: item.vgroup ? item.vgroup : [],
      }

      if (this.report.vgroup) {
        this.fleetGroupIds = this.report.vgroup.map((item) => item.id)
        this.ids = this.report.vgroup.map((item) => item.id)
      }

      if (this.report.period === 'd') this.timeDay = this.report.time
      else this.timeWeek = this.report.time
    },

    validate() {
      // Checking validation
      this.$v.$touch()
      if (this.$v.report.$error) {
        return false
      }

      return true
    },

    saveChanges() {
      let validate = this.validate()
      if (!validate) {
        return
      }

      const filter = {
        column: [
          'licensePlate',
          'serviceStatus',
          'service',
          'serviceGroup',
          'serviceOdometer',
          'startServiceAtDistance',
          'serviceDistanceDifference',
          'startServiceAtTime',
          'serviceTimeDifference',
          'workshopName',
          'workshopAddress',
          // 'workingDuration',
          // 'workingHour',
          'workshopLinkLocation',
        ],
        hourmeterColumn: [
          'licensePlate',
          'serviceStatus',
          'service',
          'serviceGroup',
          'serviceHourmeter',
          'startServiceAtHourmeter',
          'serviceHourmeterDifference',
          'workshopName',
          'workshopAddress',
          // 'workingDuration',
          // 'workingHour',
          'workshopLinkLocation',
        ],
        ...(this.isMGMAndMO? {
          refrigeratorColumn: [
            "licensePlate",
            "serviceStatus",
            "service",
            "serviceGroup",
            "serviceRefrigerator",
            "startServiceAtRefrigerator",
            "serviceRefrigeratorDifference",
            "workshopName",
            "workshopAddress",
            "workshopLinkLocation"
          ]
        }: {}),
        fileType: 'TE',
        vgroupId: this.fleetGroupIds,
      }

      const input = {
        scheduleName: this.report.scheduleName,
        period: this.report.period,
        noOfDay: this.report.period === 'd' ? null : this.report.noOfDay.value,
        time:
          this.report.period === 'd'
            ? moment(this.timeDay, 'HH:mm').format('HH:mm:ss')
            : moment(this.timeWeek, 'HH:mm').format('HH:mm:ss'),
        filter: JSON.stringify(filter),
      }

      if (!this.$route.params.id) {
        this.$apollo
          .mutate({
            mutation: CREATE_REPORT_SCHEDULER,
            variables: {
              input: {
                ...input,
                reportName: 'Pengingat Servis',
                reportKey: 'service-reminder',
              },
            },
          })
          .then((response) => {
            if (response.data.createReportScheduler.isSuccessful) {
              this.$mcToast.success(this.$t('global.alert.text.saved'))

              this.track('Success Create New Data on Email Sender')

              setTimeout(() => {
                return this.$router.push({
                  name: 'Email Alert Create',
                  params: {
                    id: response.data.createReportScheduler.reportScheduler.reportSchedulerId,
                  },
                })
              }, 500)
            } else {
              return this.$mcToast.error(response.data.createReportScheduler.message)
            }
          })
      } else {
        this.$apollo
          .mutate({
            mutation: UPDATE_REPORT_SCHEDULER,
            variables: {
              input: { ...input, id: parseInt(this.$route.params.id) },
            },
          })
          .then((response) => {
            if (response.data.updateReportScheduler.isSuccessful) {
              this.$mcToast.success(this.$t('global.alert.text.changed'))
              setTimeout(() => {
                this.$router.push({
                  name: 'Email Service Alert',
                })
              }, 500)
            } else {
              return this.$mcToast.error(response.data.updateReportScheduler.message)
            }
          })
      }
    },

    onFiltered(filteredItems) {
      this.filteredItems = filteredItems
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },

    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item[this.trackBy]]: checked }
      if (checked) {
        this.selectedIdsLength += 1
      } else {
        this.selectedIdsLength -= 1
      }
    },

    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(this.ids.map((key) => [key, true]))
        this.selectedIdsLength = this.ids.length
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },

    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },

    deleteItems() {
      const ids = Object.keys(this.selectedIds)
      let data = []
      this.report.vgroup.filter((item) => {
        if (!ids.some((el) => +el == item.id)) data.push(item)
      })

      this.report.vgroup = data
      this.fleetGroupIds = data.map((item) => item.id)

      this.selectedIds = {}
      this.selectedIdsLength = 0
    },

    selectedVGroups(value) {
      const ids = value
      ids.forEach((id) => this.fleetGroupIds.push(+id))

      this.fleetGroup.filter((item) => {
        if (ids.some((el) => +el == item.id))
          if (this.report.vgroup.filter((el) => item.id == el.id).length == 0) {
            this.report.vgroup.push(item)
          }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
ul.formulate-input-errors {
  list-style: none;
  margin: 0px !important;
  padding: 0px 0px 0px 0px !important;
  color: $red;
  font-size: $font-size-sm;
  font-style: italic;

  li.formulate-input-error::before {
    content: '' !important;
    color: $red;
    font-weight: 600;
    display: inline-block;
  }
}
</style>
